<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pipeline: { type: Object, default: null },
  activeFilters: {
    type: Object,
    default: () => ({ assignedToId: null, stageIds: [] }),
  },
});

const emit = defineEmits(['apply']);

const { t } = useI18n();
const getters = useStoreGetters();

const dialogRef = ref(null);

const localFilters = ref({
  assignedToId: null,
  stageIds: [],
});

const agents = computed(() => getters['agents/getAgents'].value || []);

const agentOptions = computed(() =>
  agents.value.map(a => ({
    value: a.id,
    label: a.name || a.email,
  }))
);

const stageOptions = computed(() =>
  (props.pipeline?.stages || []).map(s => ({ value: s.id, label: s.name }))
);

watch(
  () => props.activeFilters,
  filters => {
    localFilters.value = {
      ...filters,
      stageIds: [...(filters.stageIds || [])],
    };
  },
  { immediate: true }
);

const toggleStage = stageId => {
  const idx = localFilters.value.stageIds.indexOf(stageId);
  if (idx === -1) localFilters.value.stageIds.push(stageId);
  else localFilters.value.stageIds.splice(idx, 1);
};

const clearAll = () => {
  localFilters.value = { assignedToId: null, stageIds: [] };
};

const handleApply = () => {
  emit('apply', { ...localFilters.value });
  dialogRef.value?.close();
};

const open = () => dialogRef.value?.open();

defineExpose({ open });
</script>

<template>
  <Dialog ref="dialogRef" width="md" @close="() => {}">
    <div class="flex flex-col gap-5">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('DEALS.FILTER.TITLE') }}
      </span>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
          >
            {{ t('DEALS.FILTER.ASSIGNED_TO') }}
          </span>
          <ComboBox
            v-model="localFilters.assignedToId"
            :options="agentOptions"
            :placeholder="t('DEALS.FILTER.ALL_AGENTS')"
          />
        </div>

        <div v-if="stageOptions.length" class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
          >
            {{ t('DEALS.FILTER.STAGES') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="stage in stageOptions"
              :key="stage.value"
              type="button"
              class="px-3 py-1 rounded-full text-xs font-medium border transition-colors"
              :class="
                localFilters.stageIds.includes(stage.value)
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'bg-n-alpha-1 text-n-slate-11 border-n-weak hover:border-n-brand'
              "
              @click="toggleStage(stage.value)"
            >
              {{ stage.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DEALS.FILTER.CLEAR')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-ruby-9"
          @click="clearAll"
        />
        <Button
          :label="t('DEALS.FILTER.APPLY')"
          color="blue"
          type="submit"
          @click="handleApply"
        />
      </div>
    </template>
  </Dialog>
</template>
